import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material'
import { ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import type { GroupingContent, MatchingContent, OrderingContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'
import { exerciseRunsApi } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import { DragPickerProvider } from './dnd'
import { sourceProps, targetProps, useDragPicker } from './dragPicker'

/**
 * Рендереры типов на порядок и соответствие. Ответы: `{ matches: {левый: правый} }`,
 * `{ order: [исходные индексы] }`, `{ items: [номер группы] }`.
 */

export function MatchingRenderer({
  content,
  answers,
  onChange,
  readOnly,
  runId,
}: RendererProps<MatchingContent> & { runId?: number }) {
  const { t } = useTranslation()

  const matches = useMemo(
    () =>
      typeof answers.matches === 'object' && answers.matches !== null
        ? (answers.matches as Record<string, string>)
        : {},
    [answers.matches],
  )

  // Выбранное сейчас и мигание ошибкой: пара проверяется на сервере, поэтому
  // между кликом и ответом состояние живёт здесь
  const [pickedLeft, setPickedLeft] = useState<string | null>(null)
  const [pickedRight, setPickedRight] = useState<string | null>(null)
  const [wrong, setWrong] = useState<{ left: string; right: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lefts = useMemo(() => content.pairs.map((pair) => pair.left), [content.pairs])

  // Правая колонка приходит отдельным перемешанным списком: в паре с левым
  // элементом её отдавать нельзя — это и был бы ответ. У учителя (и после
  // сдачи) приходит исходный вид с `right` внутри пар.
  const options = useMemo(() => {
    if (Array.isArray(content.options)) return content.options

    return content.pairs.map((pair) => pair.right).filter((value): value is string => typeof value === 'string')
  }, [content.options, content.pairs])

  // Не множество значений, а счётчик: у пары синонимов с одним переводом
  // («cat» и «kitty», оба «кошка») в правой колонке две одинаковые фишки, и
  // множество гасило бы обе после первой же связи — вторую пару соединить было
  // бы нечем. Гасим ровно столько фишек каждого текста, сколько их занято.
  const usedRightCounts = useMemo(() => {
    const counts = new Map<string, number>()

    for (const right of Object.values(matches)) counts.set(right, (counts.get(right) ?? 0) + 1)

    return counts
  }, [matches])

  // Какие именно фишки правой колонки погашены: первые N по порядку среди
  // одинаковых. Индексы, а не тексты — иначе не различить дубликаты.
  const matchedRightIndexes = useMemo(() => {
    const remaining = new Map(usedRightCounts)
    const indexes = new Set<number>()

    options.forEach((value, index) => {
      const remainingCount = remaining.get(value) ?? 0

      if (remainingCount > 0) {
        remaining.set(value, remainingCount - 1)
        indexes.add(index)
      }
    })

    return indexes
  }, [options, usedRightCounts])

  const resolve = async (left: string, right: string) => {
    // Без прогона проверять некому — предпросмотр «глазами ученика» у учителя.
    // Там пара просто соединяется, без вердикта
    if (runId === undefined) {
      onChange({ ...answers, matches: { ...matches, [left]: right } })
      setPickedLeft(null)
      setPickedRight(null)

      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await exerciseRunsApi.matchAttempt(runId, left, right)

      if (result.ok) {
        onChange({ ...answers, matches: result.matches })
      } else {
        // Красным на мгновение, потом снять оба выделения
        setWrong({ left, right })
        window.setTimeout(() => setWrong(null), 700)
      }
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setPickedLeft(null)
      setPickedRight(null)
      setBusy(false)
    }
  }

  const pick = (side: 'left' | 'right', value: string) => {
    if (readOnly || busy) return

    const nextLeft = side === 'left' ? value : pickedLeft
    const nextRight = side === 'right' ? value : pickedRight

    setPickedLeft(nextLeft)
    setPickedRight(nextRight)

    if (nextLeft !== null && nextRight !== null) void resolve(nextLeft, nextRight)
  }

  const chipSx = (state: 'idle' | 'picked' | 'matched' | 'wrong') => ({
    justifyContent: 'flex-start',
    height: 'auto',
    py: 1,
    width: '100%',
    cursor: state === 'matched' || readOnly ? 'default' : 'pointer',
    ...(state === 'matched' ? { opacity: 0.45 } : {}),
    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
  })

  const stateOf = (side: 'left' | 'right', value: string, index: number) => {
    if (wrong !== null && (side === 'left' ? wrong.left : wrong.right) === value) return 'wrong' as const
    // Собственный ключ, а не просто `matches[value]`: левым элементом бывает
    // и слово вроде «constructor», а оно есть у любого объекта по прототипу —
    // фишка гасла бы, ничего не соединив
    const matchedLeft = Object.prototype.hasOwnProperty.call(matches, value)

    if (side === 'left' ? matchedLeft : matchedRightIndexes.has(index)) return 'matched' as const
    if ((side === 'left' ? pickedLeft : pickedRight) === value) return 'picked' as const

    return 'idle' as const
  }

  const column = (side: 'left' | 'right', values: string[]) => (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      {values.map((value, index) => {
        const state = stateOf(side, value, index)

        return (
          <Chip
            // Ключ по позиции, а не по тексту: одинаковые значения в колонке
            // давали бы React дублирующиеся ключи
            key={`${side}-${index}`}
            label={value}
            variant={state === 'idle' || state === 'matched' ? 'outlined' : 'filled'}
            color={state === 'wrong' ? 'error' : state === 'picked' ? 'primary' : 'default'}
            disabled={state === 'matched'}
            onClick={state === 'matched' || readOnly ? undefined : () => pick(side, value)}
            sx={chipSx(state)}
          />
        )
      })}
    </Stack>
  )

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {t('exercises.run.matchingHint')}
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={2} alignItems="flex-start">
        {column('left', lefts)}
        {column('right', options)}
      </Stack>
    </Stack>
  )
}

export function OrderingRenderer({ content, answers, onChange, readOnly }: RendererProps<OrderingContent>) {
  const { t } = useTranslation()

  // Элементы приходят текстами в перемешанном порядке — исходных индексов у
  // ученика нет, иначе сортировка по ним восстанавливала бы эталон. Ответ —
  // те же тексты в выбранной учеником последовательности.
  const items = useMemo(
    () =>
      (content.items as unknown[]).map((item) =>
        typeof item === 'string' ? item : String((item as { text?: string }).text ?? ''),
      ),
    [content.items],
  )

  const order = Array.isArray(answers.order) && answers.order.length === items.length
    ? (answers.order as string[])
    : items

  // Нетронутый виджет — тоже ответ: без этого ученик, которому раскладка
  // выпала верной, отправлял бы пустой order и получал ноль.
  useEffect(() => {
    if (readOnly || Array.isArray(answers.order)) return

    onChange({ ...answers, order: items })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, readOnly])

  const move = (position: number, delta: number) => {
    const next = [...order]
    const target = position + delta
    if (target < 0 || target >= next.length) return
    ;[next[position], next[target]] = [next[target], next[position]]
    onChange({ ...answers, order: next })
  }

  return (
    <Stack spacing={1}>
      {order.map((text, position) => (
        <Paper key={`${text}-${position}`} variant="outlined" sx={{ p: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ width: 28, color: 'text.secondary' }}>{position + 1}.</Typography>
            <Typography sx={{ flex: 1 }}>{text}</Typography>
            <IconButton
              size="small"
              disabled={readOnly || position === 0}
              onClick={() => move(position, -1)}
              aria-label={t('exercises.run.moveUp')}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={readOnly || position === order.length - 1}
              onClick={() => move(position, 1)}
              aria-label={t('exercises.run.moveDown')}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      ))}
    </Stack>
  )
}

function GroupingBody({ content, answers, onChange, readOnly }: RendererProps<GroupingContent>) {
  const { t } = useTranslation()
  const picker = useDragPicker()
  const assigned = Array.isArray(answers.items) ? (answers.items as Array<number | null>) : []

  const assign = (itemIndex: number, groupIndex: number) => {
    const next = content.items.map((_, index) => (index === itemIndex ? groupIndex : assigned[index] ?? null))
    onChange({ ...answers, items: next })
  }

  const unassigned = content.items
    .map((item, index) => ({ item, index }))
    .filter(({ index }) => assigned[index] === null || assigned[index] === undefined)

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.itemsToSort')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {unassigned.map(({ item, index }) => (
            <Chip
              key={index}
              label={item.text}
              color={picker.isPicked(String(index)) ? 'primary' : 'default'}
              disabled={readOnly}
              {...sourceProps(String(index), picker, readOnly)}
            />
          ))}
          {unassigned.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('exercises.run.allSorted')}
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {content.groups.map((group, groupIndex) => (
          <Paper
            key={groupIndex}
            variant="outlined"
            sx={{ flex: 1, p: 2, minHeight: 120, cursor: readOnly ? 'default' : 'pointer' }}
            {...targetProps((key) => assign(Number(key), groupIndex), picker, readOnly)}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {group}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {content.items.map((item, itemIndex) =>
                assigned[itemIndex] === groupIndex ? (
                  <Chip
                    key={itemIndex}
                    label={item.text}
                    color="primary"
                    disabled={readOnly}
                    onDelete={
                      readOnly
                        ? undefined
                        : () => {
                            const next = [...assigned]
                            next[itemIndex] = null
                            onChange({ ...answers, items: next })
                          }
                    }
                  />
                ) : null,
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {t('exercises.run.dragHint')}
      </Typography>
    </Stack>
  )
}

export function GroupingRenderer(props: RendererProps<GroupingContent>) {
  return (
    <DragPickerProvider>
      <GroupingBody {...props} />
    </DragPickerProvider>
  )
}
